<template>
    <ai-popup
        title="Выберите должностное лицо"
        :bottom-buttons-enabled="true"
        first-button-text="Выбрать"
        :first-button-disabled="selectButtonDisabled"
        width="60%"
        height="75%"
        v-model:visible="state.officialPopupVisible"
        @submit-click="chooseUser"
        @close-popup="hide"
    >
        <div class="flex justify-between h-full gap-8">
            <div class="h-full w-[45%] overflow-y-auto">
                <local-tree-view
                    ref="treeViewRef"
                    :data-source="usersState.departmentsWithOfficialsList"
                    :meta="meta"
                    @item-click="onItemClick"
                >
                    <template #item-template="{ data }">
                        <div class="flex items-center gap-2">
                            <img :src="`/infoenergo/assets/${data.type === 'd' ? 'department' : 'person'}.svg`" />
                            {{ data.name }}
                        </div>
                    </template>
                    <template #toolbarItems>
                        <dx-button
                            icon="/infoenergo/assets/department.svg"
                            hint="Показывать свои/все подразделения"
                            :focus-state-enabled="false"
                            :styling-mode="buttonStyle"
                            :type="buttonType"
                            @click="onFilterButtonClick"
                        />
                    </template>
                </local-tree-view>
            </div>

            <div v-if="selectedOfficial" class="grow">
                <div class="flex items-center gap-2 mb-4">
                    <h5 class="font-bold mr-2">Информация о сотруднике</h5>
                    <dx-button icon="refresh" hint="Обновить данные" @click="onRefreshClick" />
                    <dx-button icon="edit" hint="Редактировать данные о сотруднике" @click="onEditClick" />
                </div>
                <dx-form :formData="selectedOfficial" :readOnly="true">
                    <dx-group-item :col-count="2" :col-span="2">
                        <dx-simple-item
                            data-field="fio"
                            :label="{ text: 'ФИО:' }"
                            :editorOptions="{ readOnly: true }"
                        />
                        <dx-simple-item
                            data-field="e_mail"
                            :label="{ text: 'Электронная почта:' }"
                            :editorOptions="{ readOnly: true }"
                        />
                    </dx-group-item>
                    <dx-simple-item
                        data-field="department"
                        :label="{ text: 'Отдел:' }"
                        :editorOptions="{ readOnly: true }"
                    />
                    <dx-simple-item
                        data-field="namedolzh"
                        :label="{ text: 'Должность:' }"
                        :editorOptions="{ readOnly: true }"
                    />
                    <dx-group-item :colCount="2">
                        <dx-simple-item
                            data-field="mobile_phone"
                            :label="{ text: 'Мобильный телефон:' }"
                            :editorOptions="{ readOnly: true }"
                        />
                    </dx-group-item>
                </dx-form>
            </div>
        </div>
    </ai-popup>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { DxButton, DxForm } from 'devextreme-vue';
import { DxSimpleItem, DxGroupItem } from 'devextreme-vue/form';
import { dxUiNotify } from '@/utils/dx-helper';
import hideToasts from 'devextreme/ui/toast/hide_toasts';
import LocalTreeView from '@/components/local-tree-view.vue';
import AiPopup from '@/components/ai-popup/ai-popup.vue';
import usersLocalStore from './store';

import usersStore from '@/store/administration/users';

const { usersState, setOfficialUserIds, fetchOfficialUserData, getDepartmentsWithOfficialsList } = usersStore;

const router = useRouter();
const { state } = usersLocalStore;

import rolesStore from '@/store/administration/roles';

const { rolesState, getAreaListByAppKod } = rolesStore;

const selectedOfficial = ref(null);
const treeViewRef = ref();
const treeViewInstance = ref();
const buttonType = ref('danger'); //danger - own, normal - all
const buttonStyle = ref('contain'); //contain - own, outlined - all
const kod_ecls = ref(1);
const selectButtonDisabled = ref(true);
let isNotificationVisible = false;

onMounted(async () => {
    treeViewInstance.value = treeViewRef.value.treeViewInstance;
    await getDepartmentsWithOfficialsList(kod_ecls.value);
    await getAreaListByAppKod(null);
});

const onRefreshClick = async () => {
    await getDepartmentsWithOfficialsList(kod_ecls.value);
    setOfficial(usersState.userOfficialId);
};

const onFilterButtonClick = async () => {
    buttonType.value = buttonType.value === 'danger' ? 'normal' : 'danger';
    buttonStyle.value = buttonStyle.value === 'contain' ? 'outlined' : 'contain';
    kod_ecls.value = kod_ecls.value === 1 ? 0 : 1;
    await getDepartmentsWithOfficialsList(kod_ecls.value);
};

const onItemClick = ({ itemData }) => {
    if (itemData.type === 'd') return;
    setOfficial(itemData.id);
};

const onEditClick = async () => {
    await router.push({ name: 'NsiView', query: { name: 'xfmOrgStructure' } });
};

const showNotification = (message, type, duration) => {
    if (isNotificationVisible) {
        hideToasts();
        isNotificationVisible = false;
    }

    if (!isNotificationVisible) {
        dxUiNotify(message, type, duration);
        isNotificationVisible = true;

        setTimeout(() => {
            isNotificationVisible = false;
        }, duration);
    }
};

const setOfficial = async id => {
    setOfficialUserIds(id);
    await fetchOfficialUserData();
    const newUserGroupArea = rolesState.areaListByAppKod.map(area => {
        return {
            group_id: area.id,
            departments: null,
        };
    });
    state.userRequestBody = {
        ...usersState.officialUserData,
        id: null,
        official_id: id,
        locked_since: null,
        locked_until: null,
        lock_reason: '',
        locks: [],
        deleted_locks: [],
        user_type_id: 1,
        notification_email: false,
        notification_push: false,
        data_rights: newUserGroupArea,
    };
    if (!state.userRequestBody.department_rights) {
        state.userRequestBody.department_rights = [];
    }
    if (!usersState.officialUserData.e_mail) {
        showNotification(
            'Выбранного пользователя невозможно будет сохранить из-за отсутствия данных об адресе электронной почты',
            'warning',
            4000
        );
    } else {
        isNotificationVisible = false;
        hideToasts();
    }
    selectedOfficial.value = { ...state.userRequestBody };
    selectButtonDisabled.value = usersState.officialUserData.e_mail ? false : true;
};

const chooseUser = () => {
    state.userCardPopupVisible = true;
    state.officialPopupVisible = false;
    blurClearAndUnselect();
};

const hide = () => {
    state.userRequestBody = {};
    blurClearAndUnselect();
};

const blurClearAndUnselect = () => {
    selectedOfficial.value = null;
    treeViewInstance.value.collapseAll();
    treeViewInstance.value.unselectAll();
    treeViewRef.value.textBoxValue = '';
};

const meta = {
    searchHint: null,
    valueChangeEvent: 'keyup',
    keyExpr: 'key',
    displayExpr: 'name',
    parentIdExpr: 'parent_id',
    rootValue: null,
    dataScructure: 'plain',
    selectByClick: true,
    expandEvent: 'dblclick',
    selectionMode: 'single',
    itemTemplate: 'item-template',
};
</script>

<style scoped>
.dx-button.dx-button-danger {
    background-color: #dbd9d9;
}

:deep(.dx-treeview-toggle-item-visibility.dx-treeview-toggle-item-visibility-opened::before) {
    display: flex;
    align-items: center;
}

:deep(.dx-treeview-toggle-item-visibility::before) {
    display: flex;
    align-items: center;
}
</style>
